<template>
    <div v-editable="blok" data-test="NewL4ProductContent" class="l4-product-content-wrapper">
        <div v-for="{ title, text } in productContent" :key="title" class="mb-4">
            <h2 class="font-semibold text-xl mb-1">{{ title }}</h2>
            <div v-if="text" class="product-content" v-html="text" />
            <div v-else class="product-content">
                {{ $t('noInfoAvailable') }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { IL4ProductContentConfig } from '~/types/components/L4ProductContent';
import type { IRichText } from '~ui/types';
import type { IL4ApiProductFull } from '~/types/components/L4Products';

const { t } = useI18n();

const { blok } = defineProps<{ blok: IL4ProductContentConfig }>();
const product = inject<Ref<IL4ApiProductFull>>('apiProduct');

interface IProductContentItem {
    title: string;
    text: IRichText;
}

const productContent: ComputedRef<IProductContentItem[]> = computed(() => {
    // Content order - according to current layout
    const sortedValues = [
        'searchResultsSummary',
        'overview',
        'featuresAndBenefits',
        'disabledInfo',
        'additionalInfo',
        'onArrival',
        'onReturn',
        'satNavAddress',
        'directions',
    ];

    const customProductContent = {
        searchResultsSummary: 'essentials',
        overview: 'overview',
        featuresAndBenefits: 'booking_benefits',
        disabledInfo: 'disabled_info',
        additionalInfo: 'additional_info',
        onArrival: 'on_arrival',
        onReturn: 'on_return',
        satNavAddress: 'sat_nav_info',
        directions: 'directions',
    };

    return sortedValues.reduce((acc: IProductContentItem[], field) => {
        const overwriteProp = customProductContent[field];
        if (blok.overwrite_fields.includes(overwriteProp)) {
            const value =
                blok[overwriteProp] instanceof Object ? renderRichText(blok[overwriteProp]) : blok[overwriteProp];
            acc.push({ title: t(`product_content_titles.${field}`), text: value });
        }

        if (product.value?.[field]?.length && !blok.overwrite_fields.includes(overwriteProp)) {
            // calc text length without tags
            const contentItemText = product.value[field].replace(/<[^>]*>?/gm, '');
            // display content item if text length is more than 10
            if (contentItemText.length > 10) {
                // fix some broken br's
                const text = product.value[field].replaceAll('< br />', '<br/>').replaceAll('\n', '<br/>');
                acc.push({ title: t(`product_content_titles.${field}`), text: text });
            }
        }
        return acc;
    }, []);
});
</script>

<style lang="postcss" scoped>
.product-content {
    h2 {
        @apply font-semibold text-xl;
    }
    :deep(ul) {
        @apply list-disc pl-10;

        li p {
            @apply m-0;
        }
    }
    :deep(p) {
        @apply mb-2;
        word-break: break-word;
    }
    :deep(ol) {
        display: block;
        list-style-type: decimal;
        margin-block-start: 1em;
        margin-block-end: 1em;
        margin-inline-start: 0px;
        margin-inline-end: 0px;
        padding-inline-start: 40px;
    }
}
</style>
